import { skipToken } from "@reduxjs/toolkit/query";
import type Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import {
  type RecordActionAnswer,
  RecordActionRefusal,
  useCheckMemberInvitationAccessQuery,
} from "permissions/_api/record-access-api-service";
import { memberInvitationAnswer } from "permissions/_views/wording/permission-wording";
import type { User } from "users/user";

/**
 * Check access's Invite on the organization, drawn on the person page (#7151). The organization has
 * no record page, so the answer is a line under the kind that decides it, Member invitations. It is
 * the server's, from the grants the member invitation asserts
 * (`GET /legal-entities/{entityId}/access/{userId}/invite`).
 */

/** The kind whose row carries the answer. */
export const MEMBER_INVITATIONS_SLUG = "member-invitations";

/** The answer's line, in the wording module's words. */
export function memberInvitationLine(answer: RecordActionAnswer): string {
  if (answer.allowed) return memberInvitationAnswer.allowed;
  switch (answer.refusedBy) {
    case RecordActionRefusal.ReadOnly:
      return memberInvitationAnswer.readOnly;
    case RecordActionRefusal.NoMemberInvitations:
      return memberInvitationAnswer.noMemberInvitations;
    default:
      return memberInvitationAnswer.other;
  }
}

/**
 * Adds the answer's line to a row's reach lines, on the Member invitations row only. Asked about the
 * viewer's own organization, and only of a viewer who may read member permissions, which is what the
 * server's gate asks; nothing is added for anybody else, or while the answer loads.
 */
export default function usePersonMemberInvitationAnswer(
  userId: Guid,
  viewer: User | null | undefined,
): (kind: PermissionKind, reach: string[]) => string[] {
  const asked = viewer?.canReadMemberPermissions() ? { legalEntityId: viewer.legalEntityId.value, userId: userId.value } : skipToken;
  const { data, error } = useCheckMemberInvitationAccessQuery(asked);
  let line: string | undefined;
  if (data) line = memberInvitationLine(data);
  else if (error) line = memberInvitationAnswer.failed;
  return (kind, reach) => (line && kind.slug === MEMBER_INVITATIONS_SLUG ? [...reach, line] : reach);
}
